import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Check, ArrowRight, Phone, MapPin } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { SiteHeader } from '../components/home/SiteHeader';
import { SiteFooter } from '../components/home/SiteFooter';
import { FinancingCTA } from '../components/FinancingCTA';
import { Breadcrumb } from '../components/Breadcrumb';
import { CrossSellCTA, EstimateCTA } from '../components/CrossSellCTA';
import { trackClick } from '../utils/analytics';

const NoClimbFence = () => {
  useEffect(() => { document.title = 'No-Climb Fence Installation | North Texas Welding & Fabrication'; }, []);
  return (
  <div className="min-h-screen bg-white" data-testid="no-climb-fence-page">
    <Helmet>
      <meta name="description" content="No-climb wire fence installation for horses, livestock, and property security across North Texas. Safe, durable fencing with pipe construction. Springtown, Weatherford, Azle." />
      <link rel="canonical" href="https://ntxpipefence.com/no-climb-fence" />
      <meta property="og:title" content="No-Climb Fence Installation | North Texas Welding & Fabrication" />
      <meta property="og:description" content="No-climb wire fencing for horses, livestock, and property security across North Texas." />
    </Helmet>

    <SiteHeader mobileMenuOpen={false} setMobileMenuOpen={() => {}} />

    <div className="pt-24">
      <Breadcrumb items={[{ label: 'Pipe Fencing', href: '/pipe-fencing' }, { label: 'No-Climb Fence' }]} />
    </div>

    {/* Hero */}
    <section className="bg-zinc-900 py-16 relative overflow-hidden">
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnoiIHN0cm9rZT0iIzI2MjYyNiIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9nPjwvc3ZnPg==')] opacity-20"></div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">No-Climb Fence Installation</h1>
            <p className="text-zinc-400 text-lg mb-3">Horse-Safe & Livestock-Secure Wire Fencing</p>
            <p className="text-zinc-300 leading-relaxed mb-8">
              No-climb wire fencing is the preferred choice for horse owners and livestock operations that need a fence animals cannot get their hooves, legs, or heads through. Combined with our pipe fence construction, no-climb wire creates a virtually maintenance-free barrier that keeps animals safe and contained for decades. North Texas Welding and Fabrication installs no-climb fencing across the region, from small paddocks to multi-hundred-acre perimeters.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/instant-estimate" onClick={() => trackClick('Estimate - No-Climb', '/no-climb-fence')} className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold text-lg px-8 py-4 rounded-lg transition-colors">
                Get an Instant Estimate <ArrowRight size={18} />
              </Link>
              <a href="tel:8177235006" className="inline-flex items-center justify-center gap-2 border-2 border-white text-white hover:bg-white hover:text-black font-semibold text-lg px-8 py-4 rounded-lg transition-colors">
                <Phone size={18} /> 817-723-5006
              </a>
            </div>
          </div>
          <div>
            <img src="/images/no-climb-wire-fencing.jpg" alt="No-Climb Wire Fence Installation" className="rounded-xl shadow-2xl w-full object-cover aspect-[4/3]" />
          </div>
        </div>
      </div>
    </section>

    {/* Content */}
    <section className="py-16 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl font-bold text-zinc-900 mb-6">Why No-Climb Wire Fencing?</h2>
        <p className="text-zinc-700 leading-relaxed mb-4">
          No-climb wire — also called horse-safe wire or V-mesh wire — features a tight 2"x4" mesh pattern that prevents animals from stepping through, climbing over, or getting body parts caught in the fence. Unlike standard field wire with larger openings, no-climb wire eliminates the injury risks that make horse owners and livestock managers lose sleep.
        </p>
        <p className="text-zinc-700 leading-relaxed mb-4">
          We install 12.5-gauge no-climb wire on pipe fence frames — combining the proven strength of drill pipe or new pipe posts and rails with the animal-safe containment of no-climb mesh. Both pipe material options are available, and this combination is significantly more durable than wood-and-wire alternatives and requires far less maintenance over its 25-30+ year lifespan.
        </p>
        <p className="text-zinc-700 leading-relaxed mb-8">
          No-climb fencing is ideal for horse pastures, foaling paddocks, livestock holding areas, property perimeters where security matters, and any application where you need a physical barrier that animals cannot penetrate. The smooth wire surface and tight mesh pattern also discourage wildlife from pushing through, making it effective for keeping predators out and livestock in.
        </p>

        <h2 className="text-3xl font-bold text-zinc-900 mb-6">Construction Details</h2>
        <div className="grid md:grid-cols-2 gap-4 mb-8">
          {[
            '12.5-gauge galvanized no-climb wire mesh',
            '2"x4" opening pattern — hoof and limb safe',
            'Drill pipe or new pipe top rail and posts',
            'Steel posts set 3-4 feet deep in concrete',
            'Galvanized fasteners and hardware throughout',
            'Available in 4\', 5\', and 6\' heights',
            'Smooth wire surface reduces animal injury risk',
            'Can be combined with electric hot wire',
            'Suitable for horses, cattle, goats, and sheep',
            'Paint and primer options available'
          ].map(point => (
            <div key={point} className="flex items-start gap-3 p-3 bg-zinc-50 rounded-lg">
              <div className="w-5 h-5 bg-red-600 rounded-full flex items-center justify-center shrink-0 mt-0.5">
                <Check className="text-white" size={12} />
              </div>
              <p className="text-zinc-700 text-sm">{point}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <EstimateCTA heading="Get a No-Climb Fence Estimate" subtext="See preliminary pricing for your horse-safe fencing project. No obligation." pagePath="/no-climb-fence" />

    {/* FAQs */}
    <section className="py-16 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl font-bold text-zinc-900 mb-8 text-center">No-Climb Fence FAQ</h2>
        <Accordion type="single" collapsible className="space-y-3">
          {[
            { q: 'Is no-climb fencing safe for horses?', a: 'Yes. No-climb wire is specifically designed with horses in mind. The 2"x4" mesh pattern prevents hooves, legs, and heads from getting caught. When installed on pipe fence frames, it creates one of the safest fencing options available for equestrian use.' },
            { q: 'How does no-climb fence compare to board fencing for horses?', a: 'No-climb pipe fence is significantly more durable than traditional board fencing. Wood boards break, warp, and rot over time, requiring regular replacement. No-climb wire on pipe frames typically lasts 25-30+ years with minimal maintenance. It also eliminates the risk of horses chewing on or cribbing against wood rails.' },
            { q: 'Can no-climb fence contain goats and sheep?', a: 'Yes. No-climb wire is excellent for small livestock. The tight 2"x4" mesh pattern prevents smaller animals from squeezing through, and the mesh height options provide containment for goats, sheep, and other small stock.' },
            { q: 'What areas do you serve for no-climb fencing?', a: 'We install no-climb fencing throughout North Texas including Springtown, Weatherford, Azle, Decatur, Bridgeport, Aledo, Fort Worth, and surrounding communities.' },
            { q: 'Can you add no-climb wire to my existing pipe fence?', a: 'In many cases, yes. If your existing pipe fence is in good structural condition, we can add no-climb wire to the existing framework. We assess the fence during a site visit to confirm compatibility.' }
          ].map((faq, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="border border-zinc-200 rounded-lg px-6">
              <AccordionTrigger className="text-left font-semibold text-zinc-900 hover:text-red-600 py-4">{faq.q}</AccordionTrigger>
              <AccordionContent className="text-zinc-600 leading-relaxed pb-4">{faq.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>

    <CrossSellCTA
      items={[
        { eyebrow: 'Need a complete fence system?', label: 'Pipe Fencing', href: '/pipe-fencing', desc: 'Complete pipe fence installations for ranches, farms, and rural properties.' },
        { eyebrow: 'Need the fence line cleared?', label: 'Land Services', href: '/land-services', desc: 'We clear overgrown fence lines before installing your new fence.' },
        { eyebrow: 'Adding a gate?', label: 'Custom Gates', href: '/custom-gates', desc: 'Custom-built entry gates designed to match your fence.' }
      ]}
      title="Related Services"
      pagePath="/no-climb-fence"
    />

    {/* Service Areas */}
    <section className="py-12 bg-zinc-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="text-2xl font-bold text-zinc-900 mb-6">Serving North Texas</h2>
        <div className="flex flex-wrap justify-center gap-3">
          {['Springtown', 'Weatherford', 'Azle', 'Decatur', 'Bridgeport', 'Aledo', 'Fort Worth', 'Mineral Wells'].map(city => (
            <span key={city} className="bg-white border border-zinc-200 rounded-lg px-4 py-2 text-sm font-medium text-zinc-700">
              <MapPin size={12} className="inline mr-1 text-red-600" />{city}, TX
            </span>
          ))}
        </div>
      </div>
    </section>

    <FinancingCTA sourcePage="/no-climb-fence" />
    <SiteFooter />
  </div>
);};

export default NoClimbFence;
